import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { 
  Building2, MapPin, ArrowRight, Euro, ChevronRight, CheckCircle, Home, Clock, Award, 
  Target, Shield, Heart, Info, Star, ThumbsUp, Key, Zap, BarChart3, Camera, Smartphone,
  Phone, Mail, FileCheck, Calendar, ChevronDown, ChevronUp, Search, Users, HelpCircle
} from 'lucide-react';

const VendereCasaScandicci = () => {
  const [activeAccordion, setActiveAccordion] = useState<number | null>(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    message: '',
    privacy: false
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const toggleAccordion = (index: number) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real implementation, you would send the form data to a server
    console.log('Form submitted:', formData);
    setFormSubmitted(true);
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      message: '',
      privacy: false
    });
  };

  const testimonials = [
    {
      text: "La nostra esperienza con l'agenzia Molinaro immobiliare, è stata da 5 stelle, ricevendo un supporto professionale, completo e immediato da parte del titolare Federico. Ci siamo affidati a lui e possiamo dire con certezza che è una persona con un gran cuore, molto semplice, genuino e sincero, senza peli sulla lingua, con tanta esperienza e competenza sul campo, disponibile a chiarire qualsiasi dubbio e incertezze, seguendo e spiegando con molta professionalità, pazienza e sempre con il sorriso sulle labbra, tutti i passaggi burocratici che ad oggi ci hanno portato ad acquistare la casa dei nostri sogni.",
      author: "Vincenzo Marotta",
      location: "Scandicci"
    },
    {
      text: "Professionisti seri e competenti, un'esperienza impeccabile. Mi sono rivolto all'agenzia \"Molinaro Immobiliare\" per la vendita della mia casa e sono rimasto estremamente soddisfatto del servizio ricevuto. L'agente, Federico, si è dimostrato fin da subito professionale, competente e disponibile, guidandomi con pazienza e chiarezza attraverso tutte le fasi della compravendita. Ha saputo valorizzare al meglio l'immobile, trovando rapidamente acquirenti interessati e gestendo la trattativa in modo impeccabile. La Molinaro Immobiliare si distingue per la serietà, la trasparenza e l'attenzione al cliente. Consiglio vivamente questa agenzia a chiunque debba vendere o acquistare casa.",
      author: "Dario Galotti",
      location: "2 mesi fa"
    },
    {
      text: "L'agente immobiliare Federico ci ha seguito in tutto il processo di vendita con professionalità e serietà. Vi consigliamo vivamente questa agenzia che fa la differenze rispetto a molte altre!",
      author: "Giuseppina Pisano",
      location: "2 settimane fa"
    }
  ];

  const faqs = [
    {
      question: "Quanto tempo ci vuole in media per vendere casa a Scandicci?",
      answer: "Attualmente, il tempo medio di vendita a Scandicci è di circa 45-60 giorni, significativamente inferiore alla media nazionale di 6-8 mesi. Questo grazie all'alta richiesta nella zona e alla nostra strategia di marketing mirata. Le tempistiche possono variare in base alla tipologia, condizioni, prezzo e posizione specifica dell'immobile all'interno di Scandicci."
    },
    {
      question: "Quali sono i documenti necessari per vendere casa?",
      answer: "Per vendere casa a Scandicci sono necessari: atto di provenienza, planimetria catastale, visura catastale, APE (Attestato di Prestazione Energetica), eventuale documentazione condominiale, certificazioni degli impianti e documentazione urbanistica. Il nostro team ti aiuterà a raccogliere e verificare tutta la documentazione necessaria, occupandoci anche delle eventuali regolarizzazioni."
    },
    {
      question: "Come viene determinato il prezzo di vendita del mio immobile?",
      answer: "Il prezzo viene stabilito attraverso un'analisi di mercato approfondita che considera: ubicazione specifica all'interno di Scandicci, metratura, stato di conservazione, piano, esposizione, caratteristiche uniche dell'immobile, e confronto con proprietà simili recentemente vendute nella stessa area. Utilizziamo anche dati statistici aggiornati sulle compravendite a Scandicci per una valutazione accurata."
    },
    {
      question: "Quali sono le zone più richieste e di maggior valore a Scandicci?",
      answer: "Attualmente, le zone più richieste a Scandicci sono il Centro, grazie alla vicinanza alla tramvia e ai servizi, e Le Bagnese per la sua posizione strategica verso Firenze. Anche San Giusto e Casellina sono molto apprezzate dalle famiglie. I valori più elevati si registrano nel Centro (2.800-3.200€/mq) e a Le Bagnese (2.600-3.000€/mq), mentre altre zone come Vingone e San Colombano offrono un ottimo rapporto qualità/prezzo."
    },
    {
      question: "Devo essere presente durante le visite all'immobile?",
      answer: "No, non è necessario. I nostri consulenti immobiliari specializzati su Scandicci si occuperanno di tutte le visite, presentando l'immobile nel modo migliore ai potenziali acquirenti. Ti forniremo feedback dettagliati dopo ogni visita, così da tenerti sempre aggiornato sull'andamento delle attività di vendita."
    },
    {
      question: "Cosa succede se non riuscite a vendere il mio immobile?",
      answer: "Nel caso improbabile in cui non riuscissimo a vendere il tuo immobile entro il periodo concordato, rivediamo insieme la strategia: analizziamo i feedback ricevuti, valutiamo eventuali aggiustamenti di prezzo o migliorie da apportare, e ridefiniamo il piano di marketing. Il nostro obiettivo è vendere il tuo immobile al miglior prezzo possibile."
    }
  ];

  const sellingSteps = [
    {
      title: "Valutazione Gratuita",
      description: "Analizziamo il tuo immobile e il mercato di Scandicci per determinare il valore ottimale di vendita.",
      icon: Euro,
      color: "primary"
    },
    {
      title: "Marketing Strategico",
      description: "Creiamo un piano di marketing personalizzato con servizio fotografico professionale e promozione mirata.",
      icon: Target,
      color: "secondary"
    },
    {
      title: "Gestione Visite",
      description: "Organizziamo e conduciamo le visite con acquirenti pre-selezionati, valorizzando i punti di forza dell'immobile.",
      icon: Home,
      color: "primary"
    },
    {
      title: "Negoziazione Esperta",
      description: "Gestiamo le trattative per ottenere le migliori condizioni di vendita, massimizzando il valore del tuo immobile.",
      icon: Key,
      color: "secondary"
    },
    {
      title: "Assistenza Completa",
      description: "Ti accompagniamo fino al rogito, gestendo tutti gli aspetti burocratici e legali della compravendita.",
      icon: Shield,
      color: "primary"
    }
  ];

  const marketingFeatures = [
    {
      title: "Servizio Fotografico Professionale",
      description: "Immagini di alta qualità che valorizzano ogni dettaglio del tuo immobile",
      icon: Camera
    },
    {
      title: "Virtual Tour 3D",
      description: "Tour virtuale immersivo che permette ai potenziali acquirenti di visitare l'immobile online",
      icon: Smartphone
    },
    {
      title: "Pubblicazione su Portali Premium",
      description: "Annunci in evidenza sui principali portali immobiliari nazionali e locali",
      icon: Building2 
    },
    {
      title: "Social Media Marketing",
      description: "Campagne mirate su Facebook e Instagram per raggiungere potenziali acquirenti",
      icon: Info 
    },
    {
      title: "Database Acquirenti",
      description: "Accesso immediato alla nostra rete di acquirenti pre-qualificati interessati a Scandicci",
      icon: Users
    },
    {
      title: "Analisi di Performance",
      description: "Monitoraggio continuo dell'interesse e adattamento della strategia in tempo reale",
      icon: BarChart3
    }
  ];

  const animations = {
    fadeIn: {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.8 }
    },
    fadeInStaggered: (delay: number) => ({
      initial: { opacity: 0, y: 20 },
      whileInView: { opacity: 1, y: 0 },
      viewport: { once: true },
      transition: { duration: 0.5, delay }
    }),
    scaleOnHover: {
      whileHover: { scale: 1.05 },
      transition: { duration: 0.3 }
    }
  };

  return (
    <>
      <Helmet>
        <title>Vendere Casa a Scandicci | Valutazione Gratuita | Molinaro Immobiliare</title>
        <meta name="description" content="Affida a Molinaro Immobiliare la vendita della tua casa a Scandicci. Valutazione gratuita, marketing efficace e vendita al miglior prezzo in tempi rapidi." />
        <meta name="keywords" content="vendere casa scandicci, valutazione immobiliare scandicci, agenzia immobiliare scandicci, vendita immobiliare scandicci, consulenza vendita casa" />
        <link rel="canonical" href="https://molinaroimmobiliare.it/vendere-casa-scandicci" />
        <meta property="og:title" content="Vendere Casa a Scandicci | Molinaro Immobiliare" />
        <meta property="og:description" content="Affida a Molinaro Immobiliare la vendita della tua casa a Scandicci. Valutazione gratuita, marketing efficace e vendita al miglior prezzo in tempi rapidi." />
        <meta property="og:url" content="https://molinaroimmobiliare.it/vendere-casa-scandicci" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://molinaroimmobiliare.it/molinaro.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Vendere Casa a Scandicci | Molinaro Immobiliare" />
        <meta name="twitter:description" content="Affida a Molinaro Immobiliare la vendita della tua casa a Scandicci. Valutazione gratuita, marketing efficace e vendita al miglior prezzo in tempi rapidi." />
        <meta name="twitter:image" content="https://molinaroimmobiliare.it/molinaro.png" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "RealEstateService",
              "name": "Servizio Vendita Casa a Scandicci",
              "provider": {
                "@type": "RealEstateAgent",
                "name": "Molinaro Immobiliare",
                "image": "https://molinaroimmobiliare.it/molinaro.png",
                "url": "https://molinaroimmobiliare.it",
                "telephone": "+39 327 8855115",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Via Pisana 327",
                  "addressLocality": "Scandicci",
                  "postalCode": "50018",
                  "addressCountry": "IT"
                },
                "areaServed": "Scandicci"
              },
              "description": "Servizio professionale di vendita immobiliare a Scandicci con valutazione gratuita, strategie di marketing avanzate, e assistenza durante tutto il processo di vendita.",
              "serviceType": "Real Estate Selling",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "EUR",
                "name": "Valutazione Immobiliare Gratuita"
              },
              "termsOfService": "https://molinaroimmobiliare.it/termini-condizioni",
              "serviceOutput": "Vendita immobiliare al miglior prezzo di mercato in tempi rapidi"
            }
          `}
        </script>
      </Helmet>

      <div className="bg-gray-50">
        {/* ATTENTION: Hero Section - cattura l'attenzione con un forte impatto visivo */}
        <section className="relative pt-28 pb-20 bg-gradient-to-r from-primary to-primary-dark overflow-hidden">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-20">
            <img 
              src="/images/agenzia_immobiliare_scandicci1.jpeg" 
              alt="Scandicci aerial view" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-primary/70"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto text-center"
            >
              <span className="inline-block px-4 py-2 bg-secondary/20 backdrop-blur-sm text-white text-sm font-bold rounded-full mb-6">
                L'Agenzia Immobiliare di Riferimento a Scandicci dal 2017
              </span>
              <h1 className="text-5xl md:text-6xl font-monomakh text-white mb-6 leading-tight">
                La Tua Casa dei Sogni <br />Ti Aspetta a Scandicci
              </h1>
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                Molinaro Immobiliare trasforma la ricerca della casa ideale in un'esperienza semplice, trasparente e appagante.
                Con una profonda conoscenza del territorio, valorizziamo ogni immobile e ogni cliente con passione e professionalità.
              </p>
              
              <div className="flex flex-wrap justify-center gap-4 mb-12">
                <div className="bg-white/10 backdrop-blur-sm px-5 py-3 rounded-lg flex items-center">
                  <Building2 className="h-5 w-5 text-secondary mr-3" />
                  <span className="text-white">500+ immobili venduti</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-5 py-3 rounded-lg flex items-center">
                  <Award className="h-5 w-5 text-secondary mr-3" />
                  <span className="text-white">Esperti del territorio</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-5 py-3 rounded-lg flex items-center">
                  <Heart className="h-5 w-5 text-secondary mr-3" />
                  <span className="text-white">Consulenza personalizzata</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a
                  href="#contattaci"
                  className="bg-secondary hover:bg-secondary-light text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center"
                >
                  Richiedi Consulenza Gratuita
                  <ChevronRight className="ml-2 h-5 w-5" />
                </a>
                <Link
                  to="/immobili"
                  className="bg-white text-primary hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center"
                >
                  Scopri i Nostri Immobili
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* INTEREST: Expertise Section - crea interesse con dati e benefici concreti */}
        <section className="py-24 bg-white relative">
          <div className="container mx-auto px-4">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <span className="text-secondary uppercase tracking-wider font-semibold">Esperti del Territorio</span>
              <h2 className="text-4xl font-monomakh text-primary mt-2 mb-4">
                Perché Scegliere Molinaro Immobiliare a Scandicci
              </h2>
              <p className="text-gray-600 max-w-3xl mx-auto text-lg">
                Dal 2017 conosciamo ogni angolo di Scandicci e le sue dinamiche immobiliari.
                La nostra esperienza è la tua garanzia per decisioni immobiliari sicure e vantaggiose.
              </p>
            </motion.div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all border-t-4 border-primary"
              >
                <MapPin className="h-12 w-12 text-primary mb-6" />
                <h3 className="text-xl font-semibold text-primary mb-4">Specialisti di Scandicci</h3>
                <p className="text-gray-600 mb-4">
                  Conosciamo ogni zona di Scandicci nei minimi dettagli: prezzi, servizi, trasporti, scuole e tendenze di mercato.
                  Questa conoscenza territoriale ci permette di consigliarti le migliori soluzioni per le tue esigenze.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="ml-2 text-gray-700">Analisi dettagliata delle zone</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="ml-2 text-gray-700">Valutazioni realistiche di mercato</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="ml-2 text-gray-700">Consulenza territoriale personalizzata</span>
                  </li>
                </ul>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all border-t-4 border-secondary"
              >
                <Target className="h-12 w-12 text-secondary mb-6" />
                <h3 className="text-xl font-semibold text-secondary mb-4">Marketing Strategico</h3>
                <p className="text-gray-600 mb-4">
                  Strategie di marketing avanzate per vendere il tuo immobile al miglior prezzo e nei tempi più brevi.
                  Ogni immobile riceve un piano personalizzato di valorizzazione e promozione.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="ml-2 text-gray-700">Servizi fotografici professionali</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="ml-2 text-gray-700">Annunci ottimizzati sui portali</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="ml-2 text-gray-700">Promozione sui social media</span>
                  </li>
                </ul>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all border-t-4 border-primary"
              >
                <Shield className="h-12 w-12 text-primary mb-6" />
                <h3 className="text-xl font-semibold text-primary mb-4">Assistenza Completa</h3>
                <p className="text-gray-600 mb-4">
                  Ti accompagniamo in ogni fase della compravendita, dalla prima visita al rogito,
                  gestendo tutti gli aspetti burocratici e coordinando i professionisti coinvolti.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="ml-2 text-gray-700">Consulenza legale e fiscale</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="ml-2 text-gray-700">Assistenza per mutui e finanziamenti</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="ml-2 text-gray-700">Coordinamento fino al rogito</span>
                  </li>
                </ul>
              </motion.div>
            </div>
          </div>
        </section>
        
        {/* DESIRE: Success Metrics - crea desiderio mostrando i risultati e il valore */}
        <section className="py-24 bg-gray-50 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-64 h-64 rounded-br-full bg-primary/5"></div>
          <div className="absolute bottom-0 right-0 w-64 h-64 rounded-tl-full bg-secondary/5"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <span className="text-secondary uppercase tracking-wider font-semibold">I Nostri Risultati</span>
              <h2 className="text-4xl font-monomakh text-primary mt-2 mb-4">
                Numeri che Raccontano la Nostra Storia
              </h2>
              <p className="text-gray-600 max-w-3xl mx-auto text-lg">
                Dal 2017 contribuiamo a realizzare i sogni immobiliari di centinaia di famiglie a Scandicci.
                La nostra esperienza si traduce in risultati concreti e misurabili.
              </p>
            </motion.div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-center"
              >
                <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all">
                  <Calendar className="h-12 w-12 text-secondary mx-auto mb-4" />
                  <div className="text-4xl font-bold text-primary mb-2">2017</div>
                  <p className="text-gray-600">Anno di fondazione</p>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-center"
              >
                <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all">
                  <Building2 className="h-12 w-12 text-secondary mx-auto mb-4" />
                  <div className="text-4xl font-bold text-primary mb-2">500+</div>
                  <p className="text-gray-600">Immobili venduti</p>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-center"
              >
                <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all">
                  <Users className="h-12 w-12 text-secondary mx-auto mb-4" />
                  <div className="text-4xl font-bold text-primary mb-2">98%</div>
                  <p className="text-gray-600">Clienti soddisfatti</p>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="text-center"
              >
                <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all">
                  <Star className="h-12 w-12 text-secondary mx-auto mb-4" />
                  <div className="text-4xl font-bold text-primary mb-2">5/5</div>
                  <p className="text-gray-600">Valutazione media</p>
                </div>
              </motion.div>
            </div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-16 text-center"
            >
              <p className="text-gray-600 max-w-3xl mx-auto">
                Questi numeri sono il risultato della fiducia che i nostri clienti ripongono in noi ogni giorno. 
                La nostra missione è continuare a migliorare, offrendo un servizio immobiliare d'eccellenza a Scandicci.
              </p>
            </motion.div>
          </div>
        </section>
        
        {/* DESIRE: Testimonials - rafforza il desiderio con il social proof */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <span className="text-secondary uppercase tracking-wider font-semibold">Testimonianze</span>
              <h2 className="text-4xl font-monomakh text-primary mt-2 mb-4">
                Le Storie dei Nostri Clienti a Scandicci
              </h2>
              <p className="text-gray-600 max-w-3xl mx-auto text-lg">
                Le parole di chi ha già scelto Molinaro Immobiliare per il proprio progetto immobiliare a Scandicci.
              </p>
            </motion.div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-gray-50 p-8 rounded-lg relative"
                >
                  <div className="absolute top-0 right-0 transform translate-x-2 -translate-y-2 bg-secondary text-white w-10 h-10 rounded-full flex items-center justify-center">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-6 w-6">
                      <path d="M9.24 9.17C8.43 9.4 7.74 10.03 7.51 10.84C7.08 12.35 8.55 13.82 10.06 13.39C10.87 13.16 11.5 12.47 11.73 11.66C12.16 10.15 10.69 8.68 9.18 9.11L9.24 9.17ZM16.67 9.17C15.86 9.4 15.17 10.03 14.94 10.84C14.51 12.35 15.98 13.82 17.49 13.39C18.3 13.16 18.93 12.47 19.16 11.66C19.59 10.15 18.12 8.68 16.61 9.11L16.67 9.17Z" fill="currentColor"/>
                    </svg>
                  </div>
                  
                  <div className="flex space-x-1 mb-4 text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-current" />
                    ))}
                  </div>
                  
                  <p className="text-gray-600 mb-8 italic leading-relaxed">
                    "{testimonial.text.length > 300 
                      ? `${testimonial.text.substring(0, 300)}...` 
                      : testimonial.text}"
                  </p>
                  
                  <div className="flex items-center">
                    <div>
                      <h4 className="font-semibold text-primary">{testimonial.author}</h4>
                      <p className="text-gray-500 text-sm">{testimonial.location}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Il nostro processo */}
        <section className="py-24 bg-gray-50 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-64 h-64 rounded-br-full bg-primary/5"></div>
          <div className="absolute bottom-0 right-0 w-64 h-64 rounded-tl-full bg-secondary/5"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <span className="text-secondary uppercase tracking-wider font-semibold">Il Nostro Metodo</span>
              <h2 className="text-4xl font-monomakh text-primary mt-2 mb-4">
                Come Vendiamo la Tua Casa a Scandicci
              </h2>
              <p className="text-gray-600 max-w-3xl mx-auto text-lg">
                Un processo strutturato in 5 fasi per vendere il tuo immobile al miglior prezzo e nei tempi più
                brevi, con la massima trasparenza e professionalità.
              </p>
            </motion.div>
            
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-16 top-0 bottom-0 w-1 bg-primary/20 md:left-1/2 md:-ml-0.5 hidden md:block"></div>
              
              {/* Timeline steps */}
              <div className="space-y-24 relative">
                {sellingSteps.map((step, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="grid md:grid-cols-2 gap-16 items-center"
                  >
                    <div className={`md:text-right order-2 ${index % 2 === 0 ? 'md:order-1' : 'md:order-2'}`}>
                      <div className={`inline-block bg-${step.color}/10 px-3 py-1 rounded text-${step.color} text-sm font-semibold mb-3`}>
                        Fase {index + 1}
                      </div>
                      <h3 className={`text-2xl font-semibold mb-4 text-${step.color}`}>{step.title}</h3>
                      <p className="text-gray-600 text-lg mb-4">
                        {step.description}
                      </p>
                      
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <h4 className="font-medium text-gray-700 mb-2">Cosa include:</h4>
                        <ul className="space-y-2">
                          {index === 0 && (
                            <>
                              <li className="flex items-center md:justify-end">
                                <span className="text-gray-600">Sopralluogo gratuito e senza impegno</span>
                                <CheckCircle className={`md:order-1 h-5 w-5 text-${step.color} ml-0 md:ml-2 mr-2 md:mr-0`} />
                              </li>
                              <li className="flex items-center md:justify-end">
                                <span className="text-gray-600">Analisi di mercato della tua zona di Scandicci</span>
                                <CheckCircle className={`md:order-1 h-5 w-5 text-${step.color} ml-0 md:ml-2 mr-2 md:mr-0`} />
                              </li>
                              <li className="flex items-center md:justify-end">
                                <span className="text-gray-600">Valutazione professionale basata sui dati reali</span>
                                <CheckCircle className={`md:order-1 h-5 w-5 text-${step.color} ml-0 md:ml-2 mr-2 md:mr-0`} />
                              </li>
                            </>
                          )}
                          {index === 1 && (
                            <>
                              <li className="flex items-center md:justify-end">
                                <span className="text-gray-600">Servizio fotografico e home staging virtuale</span>
                                <CheckCircle className={`md:order-1 h-5 w-5 text-${step.color} ml-0 md:ml-2 mr-2 md:mr-0`} />
                              </li>
                              <li className="flex items-center md:justify-end">
                                <span className="text-gray-600">Pubblicazione su tutti i principali portali immobiliari</span>
                                <CheckCircle className={`md:order-1 h-5 w-5 text-${step.color} ml-0 md:ml-2 mr-2 md:mr-0`} />
                              </li>
                              <li className="flex items-center md:justify-end">
                                <span className="text-gray-600">Marketing sui social media mirato su Scandicci</span>
                                <CheckCircle className={`md:order-1 h-5 w-5 text-${step.color} ml-0 md:ml-2 mr-2 md:mr-0`} />
                              </li>
                            </>
                          )}
                          {index === 2 && (
                            <>
                              <li className="flex items-center md:justify-end">
                                <span className="text-gray-600">Selezione di acquirenti realmente interessati</span>
                                <CheckCircle className={`md:order-1 h-5 w-5 text-${step.color} ml-0 md:ml-2 mr-2 md:mr-0`} />
                              </li>
                              <li className="flex items-center md:justify-end">
                                <span className="text-gray-600">Presentazione professionale dell'immobile</span>
                                <CheckCircle className={`md:order-1 h-5 w-5 text-${step.color} ml-0 md:ml-2 mr-2 md:mr-0`} />
                              </li>
                              <li className="flex items-center md:justify-end">
                                <span className="text-gray-600">Feedback dettagliato dopo ogni visita</span>
                                <CheckCircle className={`md:order-1 h-5 w-5 text-${step.color} ml-0 md:ml-2 mr-2 md:mr-0`} />
                              </li>
                            </>
                          )}
                          {index === 3 && (
                            <>
                              <li className="flex items-center md:justify-end">
                                <span className="text-gray-600">Analisi delle offerte ricevute</span>
                                <CheckCircle className={`md:order-1 h-5 w-5 text-${step.color} ml-0 md:ml-2 mr-2 md:mr-0`} />
                              </li>
                            </>
                          )}
                        </ul>
                      </div>
                    </div>
                    
                    <div className={`relative order-1 ${index % 2 === 0 ? 'md:order-2' : 'md:order-1'}`}>
                      {/* Timeline dot */}
                      <div className={`absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-${step.color} rounded-full flex items-center justify-center z-10 hidden md:flex`}>
                        <step.icon className="h-6 w-6 text-white" />
                      </div>
                      
                      <img 
                        src={index === 0 ? "/images/agenzia_immobiliare_scandicci1.jpeg" : 
                             index === 1 ? "/images/agenzia_immobiliare_scandicci5.jpeg" :
                             index === 2 ? "/images/agenzia_immobiliare_scandicci2.jpeg" :
                             index === 3 ? "/images/agenzia3.jpeg" :
                             "/images/agenzia_immobiliare_scandicci10.jpeg"} 
                        alt={step.title} 
                        className="rounded-xl shadow-xl w-full h-80 object-cover"
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Strategie di marketing */}
        <section className="py-24 bg-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-1/3 h-full opacity-10">
            <img 
              src="https://images.unsplash.com/photo-1557682250-f37ae611f8ff?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" 
              alt="" 
              className="w-full h-full object-cover"
            />
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mb-16">
              <span className="text-secondary uppercase tracking-wider font-semibold">Strategie Avanzate</span>
              <h2 className="text-4xl font-monomakh text-primary mt-2 mb-6">
                Piano di Marketing <br />Personalizzato
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                Sviluppiamo un piano di marketing su misura per il tuo immobile, 
                combinando tecniche tradizionali e digitali per massimizzare la visibilità 
                e attrarre gli acquirenti giusti.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {marketingFeatures.map((service, index) => (
                <motion.div
                  key={index}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0 }
                  }}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                >
                  <div className="bg-primary/5 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                    <service.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-primary">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </motion.div>
              ))}
            </div>
            
            {/* Stats section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-20 bg-primary rounded-xl overflow-hidden shadow-xl"
            >
              <div className="grid md:grid-cols-2 items-center">
                <div className="p-10 text-white">
                  <h3 className="text-3xl font-monomakh mb-6">Risultati Tangibili</h3>
                  <p className="text-gray-200 mb-8 text-lg">
                    Il nostro approccio orientato ai risultati ci permette di raggiungere obiettivi concreti:
                  </p>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <div className="text-4xl font-bold text-secondary mb-2">+65%</div>
                      <p className="text-gray-300">Visualizzazioni rispetto alle agenzie tradizionali</p>
                    </div>
                    <div>
                      <div className="text-4xl font-bold text-secondary mb-2">92%</div>
                      <p className="text-gray-300">Delle vendite concluse al prezzo richiesto</p>
                    </div>
                    <div>
                      <div className="text-4xl font-bold text-secondary mb-2">-45%</div>
                      <p className="text-gray-300">Tempi di vendita ridotti rispetto alla media</p>
                    </div>
                    <div>
                      <div className="text-4xl font-bold text-secondary mb-2">500+</div>
                      <p className="text-gray-300">Potenziali acquirenti nel nostro database</p>
                    </div>
                  </div>
                </div>
                <div className="hidden md:block h-full">
                  <img 
                    src="/images/federico.jpg" 
                    alt="Federico Molinaro"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </section>
        
        {/* FAQ Section */}
        <section className="py-24 bg-gray-50">
          <div className="container mx-auto px-4">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <span className="text-secondary uppercase tracking-wider font-semibold">Domande Frequenti</span>
              <h2 className="text-4xl font-monomakh text-primary mt-2 mb-4">
                Tutto Quello che Devi Sapere
              </h2>
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                Abbiamo raccolto le domande più comuni che ci vengono poste dai proprietari che vogliono vendere casa.
                Se hai ulteriori dubbi, non esitare a contattarci.
              </p>
            </motion.div>
            
            <div className="max-w-3xl mx-auto divide-y divide-gray-200">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="py-6"
                >
                  <button
                    onClick={() => toggleAccordion(index)}
                    className="flex justify-between items-center w-full text-left focus:outline-none"
                  >
                    <h3 className="text-xl font-semibold text-primary flex items-center">
                      <HelpCircle className="h-5 w-5 mr-2 text-secondary flex-shrink-0" />
                      <span>{faq.question}</span>
                    </h3>
                    {activeAccordion === index ? (
                      <ChevronUp className="h-5 w-5 text-primary" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-primary" />
                    )}
                  </button>
                  
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{
                      height: activeAccordion === index ? 'auto' : 0,
                      opacity: activeAccordion === index ? 1 : 0
                    }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <p className="mt-4 text-gray-600 pl-7">{faq.answer}</p>
                  </motion.div>
                </motion.div>
              ))}
            </div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mt-16 bg-white p-8 rounded-lg shadow-md"
            >
              <div className="text-center">
                <h3 className="text-xl font-semibold text-primary mb-4">Hai altre domande?</h3>
                <p className="text-gray-600 mb-6">
                  Contattaci per una consulenza gratuita e senza impegno. Siamo qui per aiutarti a vendere il tuo immobile
                  nelle migliori condizioni possibili.
                </p>
                <a
                  href="#valutazione"
                  className="inline-block bg-primary hover:bg-primary-light text-white font-semibold px-6 py-3 rounded-lg transition-colors"
                >
                  Richiedi una consulenza
                </a>
              </div>
            </motion.div>
          </div>
        </section>
        
        {/* ACTION: Contact Form - chiara call to action per convertire */}
        <section id="contattaci" className="py-24 bg-primary relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="" className="w-full h-full object-cover" />
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-xl overflow-hidden">
              <div className="grid md:grid-cols-2">
                <div className="p-10">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                  >
                    <h2 className="text-3xl font-monomakh text-primary mb-6">Richiedi una Consulenza Gratuita</h2>
                    <p className="text-gray-600 mb-8">
                      Sei pronto a scoprire il valore del tuo immobile a Scandicci o a trovare 
                      la casa dei tuoi sogni? Compila il form e ti ricontatteremo entro 24 ore.
                    </p>
                    
                    <form className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Nome e Cognome*</label>
                          <input 
                            type="text" 
                            required 
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Telefono*</label>
                          <input 
                            type="tel" 
                            required 
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email*</label>
                        <input 
                          type="email" 
                          required 
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Sono interessato a</label>
                        <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary">
                          <option>Acquistare un immobile a Scandicci</option>
                          <option>Vendere un immobile a Scandicci</option>
                          <option>Affittare un immobile a Scandicci</option>
                          <option>Valutazione gratuita del mio immobile</option>
                          <option>Altro (specificare nel messaggio)</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Messaggio</label>
                        <textarea 
                          rows={4} 
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                        ></textarea>
                      </div>
                      
                      <div className="flex items-start">
                        <input 
                          type="checkbox" 
                          id="privacy" 
                          required
                          className="h-4 w-4 text-primary border-gray-300 rounded mt-1" 
                        />
                        <label htmlFor="privacy" className="ml-2 block text-xs text-gray-500">
                          Autorizzo il trattamento dei miei dati personali ai sensi del GDPR (UE 2016/679)
                        </label>
                      </div>
                      
                      <button
                        type="submit"
                        className="w-full bg-primary hover:bg-primary-light text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                      >
                        Invia Richiesta
                      </button>
                    </form>
                  </motion.div>
                </div>
                
                <div className="hidden md:block">
                  <img 
                    src="/images/agenzia5.jpeg" 
                    alt="Scandicci panorama" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default VendereCasaScandicci;